import { ref, reactive, toRefs, set as vueSet, nextTick } from 'vue'
import { isArray } from '@/common/js/utils'
import { getLocal, setLocal } from '@/common/js/api'
import { adAccount, getSysMember } from '@/api/common.model'
import { queryTaskByPage, retryTask as retryTaskApi } from '@/api/material.model'
import tableColumns from '../../data/library_upload_column'
import { UPLOAD_TYPE_LIST } from '../const'
import { IMAGE_TYPES_LIST } from '../../uploading/consts'
export const EXTRA_IMAGE_TYPES_LIST = [
  { id: 3, name: '封面' },
  { id: 101, name: '图片组件' },
  { id: 102, name: '视频组件' },
  { id: 108, name: '组图组件' }
]
const FULL_IMAGE_TYPES_LIST = IMAGE_TYPES_LIST.concat(EXTRA_IMAGE_TYPES_LIST.map(t => ({ type: t.id, label: t.name })))

// 1. Table Logic Hook
export const useUploadTable = (proxy) => {
  const state = reactive({
    loading: false,
    pageIndex: 1,
    pageSize: parseInt(getLocal('PAGESIZE')) || 10,
    total: 0,
    tableData: [],
    tableColumns,
    height: 330,
    params: {}
  })

  const getData = async (filter) => {
    let params = {
      pageIndex: state.pageIndex,
      pageSize: state.pageSize
    }

    if (filter) params = Object.assign(params, filter)
    else params = Object.assign(state.params, params)

    // Deep copy params to state
    state.params = JSON.parse(JSON.stringify(params))

    state.loading = true
    try {
      const data = await queryTaskByPage(params)
      if (typeof data !== 'symbol') {
        state.loading = false
        if (!data) return

        const { list, page_info } = data
        if (isArray(list) && page_info) {
          const { total_number } = page_info
          state.tableData = list.map((ret) => {
            if (ret.coverUrl && ret.coverUrl.indexOf(',') > -1) {
              const imgs = ret.coverUrl.split(',')
              ret.double = imgs.length === 2
              ret.multiple = imgs.length > 2
              ret.cover = imgs[0]
            } else ret.cover = ret.coverUrl
            return {
              ...ret,
              video: Boolean(ret.materialType === 2),
              materialSourceStr: UPLOAD_TYPE_LIST.find(i => i.value === ret.materialSource)?.label || ''
            }
          })
          state.total = total_number || 0
        } else {
          state.tableData = []
          state.total = 0
        }

        nextTick(() => {
          const table = proxy.$refs.libraryUploadTable
          if (table) table.doLayout()
        })
      }
    } catch (e) {
      state.loading = false
      state.tableData = []
      state.total = 0
    }
  }

  const changePageIndex = (index) => {
    state.pageIndex = index
    getData()
  }

  const changePageSize = (size) => {
    setLocal('PAGESIZE', size)
    state.pageSize = size
    state.pageIndex = 1
    getData()
  }

  const filterData = (params) => {
    state.pageIndex = 1
    getData(params)
  }

  return {
    ...toRefs(state),
    getData,
    changePageIndex,
    changePageSize,
    filterData
  }
}

// 2. Dictionaries Hook
export const useUploadDictionaries = () => {
  const state = reactive({
    operatorData: [],
    accountData: [],
    stateData: [
      { id: 3, name: '失败' },
      { id: 2, name: '成功' },
      { id: 0, name: '未开始' },
      { id: 1, name: '执行中' }
    ]
  })

  const preloadData = async () => {
    try {
      const [accountRes, operatorRes] = await Promise.all([
        adAccount({ authFlag: 1 }),
        getSysMember()
      ])

      if (isArray(accountRes)) state.accountData = accountRes
      if (isArray(operatorRes)) state.operatorData = operatorRes
    } catch (e) {
      console.error(e)
    }
  }

  return {
    ...toRefs(state),
    preloadData
  }
}

// 3. Operations Hook
export const useUploadOperations = (proxy, getData) => {
  const retryTask = async (row) => {
    const { taskId } = row

    vueSet(row, 'retring', true)
    try {
      const data = await retryTaskApi({ taskId })
      if (typeof data !== 'symbol') {
        vueSet(row, 'retring', false)
        if (data) getData()
      }
    } catch (e) {
      vueSet(row, 'retring', false)
    }
  }

  const showMedia = (param) => {
    const { src, video, row } = param
    const { videoUrl, coverUrl } = row
    const imgs = coverUrl && coverUrl.includes(',') ? coverUrl.split(',') : [coverUrl]
    proxy.$Gallery.open({ src, video, videoUrl, imgs })
  }

  return {
    retryTask,
    showMedia
  }
}

// 4. Formatters Hook
export const useUploadFormatters = (stateData) => {
  const toTaskStatus = (cellValue, row) => {
    const index = stateData.value.findIndex((ret) => ret.id === cellValue)
    const statusName = index < 0 ? '未知' : stateData.value[index].name
    return row.taskStatus !== 2 && row.msg ? `${statusName}，${row.msg}` : statusName
  }

  const toMaterType = (cellValue) => {
    const { label } = FULL_IMAGE_TYPES_LIST.find((ret) => ret.type === cellValue) || {}
    return label || '未知'
  }

  const toUpType = (cellValue) => {
    return ['未知', '上传', '推送'][cellValue] || '未知'
  }

  return {
    toTaskStatus,
    toMaterType,
    toUpType
  }
}
